import { numberOnly } from '../../../masks/masks'
import { isValidDateTime } from '../../../core/functions/dateTime'
import {
  TPersonType,
  TTenantAddressRegister,
  TTenantRegister,
} from '../../../core/types/Tenant'

export type TTenantOnboardingMode =
  | 'complete'
  | 'create-bank-account'
  | 'update-registration'

const individualSteps = [
  'Cadastro',
  'Complemento do Cadastro',
  'Dados de Endereço',
]

const corporationSteps = [
  'Cadastro',
  'Dados da Empresa',
  'Endereço da Empresa',
  'Dados do Representante Legal',
  'Endereço do Representante Legal',
]

const bankAccountStep = 'Dados Bancário para Recebimento'

export const isPhoneComplete = (phone?: string | null) => {
  const digits = numberOnly(phone ?? '')
  return digits.length >= 10 && digits.length <= 11
}

const hasValidDate = (date?: string | Date | null) => {
  if (!date) return false
  return isValidDateTime(date)
}

const hasText = (value?: string | null) => Boolean(value?.trim())

const hasCompleteAddress = (address?: TTenantAddressRegister | null) => {
  if (!address) return false

  return [
    address.street,
    address.complement,
    address.number,
    address.neighborhood,
    address.city,
    address.state,
    address.zipCode,
    address.referencePoint,
  ].every(hasText)
}

export const isOnboardingRequired = (
  recipientData?: TTenantRegister | null
) => {
  if (!recipientData) return true
  if (!recipientData.bankAccount) return true
  if (!isPhoneComplete(recipientData.phone)) return true

  if (recipientData.personType === TPersonType.Individual) {
    if (!hasValidDate(recipientData.birthDate)) return true
    if (!hasCompleteAddress(recipientData.address)) return true

    return !hasText(recipientData.professionalOccupation)
  }

  if (recipientData.personType === TPersonType.Corporation) {
    if (!hasValidDate(recipientData.birthDate)) return true
    if (!hasCompleteAddress(recipientData.mainAddress ?? recipientData.address))
      return true
    if (!recipientData.managingPartner) return true
    if (!isPhoneComplete(recipientData.managingPartner.phone)) return true
    if (!hasCompleteAddress(recipientData.managingPartner.address)) return true

    return !hasValidDate(recipientData.managingPartner.birthDate)
  }

  return true
}

export const getOnboardingMode = (
  recipientData?: TTenantRegister | null
): TTenantOnboardingMode => {
  if (!recipientData || !recipientData.bankAccount) {
    return 'create-bank-account'
  }

  if (isOnboardingRequired(recipientData)) {
    return 'update-registration'
  }

  return 'complete'
}

export const getTenantOnboardingSteps = (
  personType: TPersonType,
  mode: TTenantOnboardingMode
) => {
  const steps =
    personType === TPersonType.Corporation
      ? [...corporationSteps]
      : [...individualSteps]

  if (mode === 'create-bank-account') {
    steps.push(bankAccountStep)
  }

  return steps
}
